import { useEffect, useState } from "react";
import { Button, Select, Tooltip, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { FilePdfOutlined } from "@ant-design/icons";

import {
  getAllClientsNoPagination,
  getClientIdByToken,
  useWorkflowContainerThree,
  validTaskCodeByToken,
  generateWfOnePdfReportbyId,
  generateWfTwoPdfReportbyId,
  generateWfThreePdfReportbyId,
} from "../../../../../libs/apis";
import { ClientI, WorkflowContainerI } from "../../../../../libs/interfaces";
import { SendReport } from "../../../../../libs/shared";
import { Environments } from "../../../../../libs/enums";
import { ShowContainer as ShowOne } from "../../../../../components/workflow-one/components/list/show-container";
import { ShowContainer as ShowTwo } from "../../../../../components/workflow-two/components/list/show-container";
import { ShowContainer as ShowThree } from "../../../../../components/workflow-three/components/list/show-container";
import {
  DashboardTable,
  PAGE_SIZE,
  PeriodT,
  usePdfDownload,
} from "./dashboard-table";

const SORTING = [{ id: "id", desc: true }];

const pdfGenerators: Record<string, (id: number) => Promise<{ data?: any }>> = {
  "Proceso 1": generateWfOnePdfReportbyId,
  "Proceso 2": generateWfTwoPdfReportbyId,
  "Proceso 3": generateWfThreePdfReportbyId,
};

const wfType = (processType: string) =>
  processType === "Proceso 1"
    ? "one"
    : processType === "Proceso 2"
    ? "two"
    : "three";

export const TableComponent = ({ typeProcess }: { typeProcess: string }) => {
  const [period, setPeriod] = useState<PeriodT>("hoy");
  const [pageIndex, setPageIndex] = useState(0);
  const [search, setSearch] = useState("");
  const [client, setClient] = useState<number | null>(null);
  const [clients, setClients] = useState<ClientI[]>([]);
  const [showClients, setShowClients] = useState(false);

  const { download, loadingId, contextHolder } = usePdfDownload("report");

  const { data, isLoading, isRefetching, refetch } =
    useWorkflowContainerThree({
      sorting: SORTING,
      pagination: { pageIndex, pageSize: PAGE_SIZE },
      globalFilter: search,
      client,
      typeProcess,
      period,
    }).workflowContainerThreeQuery;

  useEffect(() => {
    setPageIndex(0);
  }, [typeProcess]);

  useEffect(() => {
    (async () => {
      try {
        const { data: dataClient } = await getClientIdByToken();
        if (dataClient) setClient(dataClient);

        const { data: dataTask } = await validTaskCodeByToken(
          Environments.TASK_WORKFLOW_ONE_ASIGNAR
        );
        if (!dataTask?.status) return;
        setShowClients(true);
        const { data: dataClients } = await getAllClientsNoPagination();
        if (dataClients) setClients(dataClients);
      } catch (err: any) {
        message.error(err.toString());
      }
    })();
  }, []);

  const handlePdf = (row: WorkflowContainerI) => {
    const generator = pdfGenerators[row.processType ?? ""];
    if (generator) download(generator, row.id!, row.container);
  };

  const columns: ColumnsType<WorkflowContainerI> = [
    {
      title: "Contenedor",
      dataIndex: "container",
      render: (value, row) => <a onClick={() => handlePdf(row)}>{value}</a>,
    },
    { title: "Cliente", dataIndex: "client", responsive: ["md"] },
    { title: "Creado por", dataIndex: "userCreation" },
    { title: "Tipo Proceso", dataIndex: "processType", responsive: ["sm"] },
    { title: "Hora", dataIndex: "hour", responsive: ["sm"] },
    { title: "Fecha de creación", dataIndex: "createdAt" },
    {
      title: "",
      key: "actions",
      fixed: "right",
      width: 130,
      render: (_, row) => (
        <div style={{ display: "flex", gap: 6 }}>
          <Tooltip arrow={false} title="Pdf">
            <Button
              size="small"
              type="dashed"
              icon={<FilePdfOutlined />}
              loading={loadingId === row.id}
              disabled={loadingId !== null}
              onClick={() => handlePdf(row)}
            />
          </Tooltip>
          <SendReport id={row.id!} typeWf={wfType(row.processType ?? "")} />
          {row.processType === "Proceso 1" ? <ShowOne id={row.id!} /> : null}
          {row.processType === "Proceso 2" ? <ShowTwo id={row.id!} /> : null}
          {row.processType === "Proceso 3" ? <ShowThree id={row.id!} /> : null}
        </div>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <DashboardTable<WorkflowContainerI>
        columns={columns}
        data={data?.data?.dato ?? []}
        total={data?.data?.total ?? 0}
        loading={isLoading || isRefetching}
        pageIndex={pageIndex}
        period={period}
        onPeriodChange={setPeriod}
        onPageChange={setPageIndex}
        onSearch={setSearch}
        onRefresh={refetch}
        extra={
          showClients ? (
            <Select
              showSearch
              allowClear
              style={{ minWidth: 180, flex: "1 1 180px", maxWidth: 250 }}
              placeholder="Cliente"
              optionFilterProp="label"
              options={clients.map((el) => ({ label: el.name, value: el.id }))}
              onChange={(value) => {
                setPageIndex(0);
                setClient(value ?? null);
              }}
            />
          ) : null
        }
      />
    </>
  );
};
